/** Authentication context: session state, MFA flow, tenant switching. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, api } from '../api/client';
import type { Me } from '../api/types';

export type AuthStage = 'loading' | 'anonymous' | 'mfa_required' | 'mfa_enrollment' | 'ready';

interface AuthState {
  stage: AuthStage;
  me: Me | null;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  enrollMfa: () => Promise<{ secret: string; otpauth_uri: string }>;
  confirmMfa: (code: string) => Promise<string[]>;
  selectTenant: (tenantId: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [stage, setStage] = useState<AuthStage>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const profile = await api.me();
      setMe(profile);
      setStage('ready');
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'auth.mfa_required') {
        setStage('mfa_required');
      } else {
        setMe(null);
        setStage('anonymous');
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const result = await api.login(email, password);
        if (result.status === 'authenticated') {
          await load();
        } else if (result.status === 'mfa_enrollment_required') {
          setStage('mfa_enrollment');
        } else {
          setStage('mfa_required');
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.code : 'server.error');
        throw err;
      }
    },
    [load],
  );

  const value = useMemo<AuthState>(
    () => ({
      stage,
      me,
      error,
      login,
      verifyMfa: async (code: string) => {
        setError(null);
        try {
          const profile = await api.verifyMfa(code);
          setMe(profile);
          setStage('ready');
        } catch (err) {
          setError(err instanceof ApiError ? err.code : 'server.error');
          throw err;
        }
      },
      enrollMfa: () => api.enrollMfa(),
      confirmMfa: async (code: string) => {
        const result = await api.confirmMfa(code);
        await load();
        return result.recovery_codes;
      },
      selectTenant: async (tenantId: string) => {
        const profile = await api.selectTenant(tenantId);
        setMe(profile);
        setStage('ready');
      },
      logout: async () => {
        await api.logout();
        setMe(null);
        setStage('anonymous');
      },
      refresh: load,
      can: (permission: string) => Boolean(me?.permissions.includes(permission)),
    }),
    [stage, me, error, login, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
